import React, { useState, useEffect, useRef, SyntheticEvent } from "react";
import { Box, Grid, SxProps, Theme } from "@mui/material";
import { ImageControlsStyles } from "../../types";

export interface IViewerProps {
  zoom: number;
  panDiagonalX: number;
  panDiagonalY: number;
  styles: Record<ImageControlsStyles, SxProps<Theme>>;
  setZoom: (z: number) => void;
  onPan: (x: number, y: number) => void;
  onWheel?(e: React.WheelEvent<EventTarget>): void | false;
  rotation: number;
  imgHeight: number;
  image: string;
  alt: string | undefined;
  ref: any;
  flip: boolean;
}

export default function ImageViewer(props: IViewerProps) {
  const { panDiagonalX, panDiagonalY, zoom, styles, flip, onPan, rotation, imgHeight, onWheel, ref, alt, image } =
    props;

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [dragData, setDragData] = useState({
    panDiagonalX,
    panDiagonalY,
    x: 0,
    y: 0,
  });
  const [matrixData, setMatrixData] = useState([zoom, 0, 0, zoom, panDiagonalX, panDiagonalY]);
  const [imageSize, setImageSize] = useState({});

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > naturalHeight) {
      setImageSize({
        maxWidth: "100%",
      });
    } else {
      setImageSize({
        height: imgHeight,
      });
    }
  };

  const panWrapper = useRef<HTMLDivElement>();
  const panContainer = useRef<HTMLDivElement>();

  useEffect(() => {
    const newMatrixData = [...matrixData];

    newMatrixData[0] = zoom || newMatrixData[0];
    newMatrixData[3] = zoom || newMatrixData[3];

    setMatrixData(newMatrixData);
  }, [zoom]);

  const onTouchStart = (e: React.TouchEvent<EventTarget>) => panStart(e.touches[0].pageX, e.touches[0].pageY, e);

  const onTouchEnd = () => onMouseUp();

  const onTouchMove = (e: React.TouchEvent<EventTarget>) => updateMousePosition(e.touches[0].pageX, e.touches[0].pageY);

  const onMouseDown = (e: React.MouseEvent<EventTarget>) => panStart(e.pageX, e.pageY, e);

  const panStart = (
    pageX: number,
    pageY: number,
    event: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>
  ) => {
    setDragData({
      panDiagonalX: matrixData[4],
      panDiagonalY: matrixData[5],
      x: pageX,
      y: pageY,
    });

    setIsMouseDown(true);

    if (panWrapper.current) panWrapper.current.style.cursor = "move";

    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    event.preventDefault();
  };

  const onMouseUp = () => panEnd();

  const panEnd = () => {
    setIsMouseDown(false);

    if (panWrapper.current) panWrapper.current.style.cursor = "";

    onPan(matrixData[4], matrixData[5]);
  };

  const onMouseMove = (e: React.MouseEvent<EventTarget>) => updateMousePosition(e.pageX, e.pageY);

  const updateMousePosition = (pageX: number, pageY: number) => {
    if (!isMouseDown) return;

    setMatrixData(getNewMatrixData(pageX, pageY));

    if (panContainer.current) panContainer.current.style.transform = `matrix(${matrixData.toString()})`;
  };

  const getNewMatrixData = (x: number, y: number) => {
    const currentMatrixData = [...matrixData];

    const deltaX = dragData.x - x;
    const deltaY = dragData.y - y;

    currentMatrixData[4] = dragData.panDiagonalX - deltaX;
    currentMatrixData[5] = dragData.panDiagonalY - deltaY;

    return currentMatrixData;
  };

  return (
    <Grid
      container
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseMove={onMouseMove}
      ref={(ref: HTMLDivElement) => (ref ? (panWrapper.current = ref) : null)}
      sx={{ ...styles.viewer, overflow: "hidden" }}
    >
      <Grid
        item
        onWheel={onWheel ? onWheel : () => {}}
        ref={(ref: HTMLDivElement) => (ref ? (panContainer.current = ref) : null)}
      >
        <Box
          component="img"
          sx={{
            ...imageSize,
            transform: `rotate(${rotation * 90}deg) scaleX(${flip ? -1 : 1}) matrix(${matrixData.toString()})`,
          }}
          src={image}
          alt={alt}
          ref={ref}
          onLoad={handleImageLoad}
        />
      </Grid>
    </Grid>
  );
}
